import { IconButton } from '@artosera/ui';
import { X } from 'lucide-react';
import type { JSX } from 'react';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useContacts, type ContactPresente } from '../../api/contacts.js';
import { useDebounce } from '../oeuvres/useDebounce.js';
import styles from './RechercheContact.module.css';

export interface RechercheContactProps {
  galerieSlug: string;
  contact: ContactPresente | null;
  onChoisir: (contact: ContactPresente) => void;
  onEffacer: () => void;
  /**
   * Les résultats flottent au-dessus de la page (rangée de filtres) au lieu
   * de pousser le contenu (dialogue). Présentation seulement.
   */
  flottant?: boolean;
}

/**
 * Recherche d'un contact du carnet (§ 17.4 : « `contactId` désigne un
 * contact DU CARNET »). Un seul choix à la fois : sélectionner remplace,
 * jamais n'ajoute — à la différence de la recherche d'artistes ou d'œuvres
 * d'une exposition (`OeuvresArtistesTab`), une facture n'a qu'un client.
 */
export function RechercheContact({
  galerieSlug,
  contact,
  onChoisir,
  onEffacer,
  flottant = false,
}: RechercheContactProps): JSX.Element {
  const { t } = useTranslation(['regie', 'commun']);
  const [recherche, setRecherche] = useState('');
  const rechercheDebattue = useDebounce(recherche, 300);

  const resultats = useContacts(
    galerieSlug,
    { q: rechercheDebattue || undefined, tri: 'nom', parPage: 8 },
    { enabled: rechercheDebattue.trim().length > 0 && contact === null },
  );

  if (contact) {
    return (
      <div className={styles.choisi}>
        <div>
          <span className={styles.choisiNom}>{contact.nomAffiche}</span>
          {contact.emails[0] ? (
            <span className={styles.choisiMeta}>{contact.emails[0]}</span>
          ) : null}
        </div>
        <IconButton
          variant="outline"
          icon={<X aria-hidden />}
          aria-label={t('regie:ventes.nouvelle.contactChanger')}
          size="sm"
          onClick={onEffacer}
        />
      </div>
    );
  }

  return (
    <div className={styles.recherche} data-flottant={flottant ? 'oui' : undefined}>
      <input
        type="search"
        className={styles.saisie}
        placeholder={t('regie:ventes.nouvelle.contactRecherche')}
        aria-label={t('regie:ventes.nouvelle.contactRecherche')}
        value={recherche}
        onChange={(event) => setRecherche(event.target.value)}
      />
      {rechercheDebattue.trim().length > 0 ? (
        <div className={styles.resultats}>
          {resultats.isLoading ? (
            <p className={styles.vide}>{t('commun:chargement')}</p>
          ) : (resultats.data?.contacts.length ?? 0) === 0 ? (
            <p className={styles.vide}>{t('regie:ventes.nouvelle.contactAucunResultat')}</p>
          ) : (
            resultats.data?.contacts.map((candidat) => (
              <button
                key={candidat.id}
                type="button"
                className={styles.ligne}
                onClick={() => {
                  onChoisir(candidat);
                  setRecherche('');
                }}
              >
                <span>{candidat.nomAffiche}</span>
                <span className={styles.ligneMeta}>{candidat.emails[0] ?? '—'}</span>
              </button>
            ))
          )}
        </div>
      ) : null}
    </div>
  );
}
